<template>
  <v-container fluid>
    <v-row v-if="isLoading">
      <v-col>
        <loading-spinner></loading-spinner>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12" md="6">
        <v-card class="mb-3">
          <v-card-text>
            <v-row class="mt-0">
              <v-col cols="12" md="6">
                <v-select
                  v-model="filterJournalType"
                  :items="journalTypeFilterOptions"
                  :label="$t('label.archive_filter')"
                  dense
                  hide-details
                  @change="handleFilterChange"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <paginated-table
          :data="archives"
          :columns="tableColumns"
          :title="$t('title.journal_archive')"
          @updatePagination="updatePagination"
        ></paginated-table>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <card-header :title="$t('title.write_form')"></card-header>
          <v-card-text>
            <v-form ref="form" v-model="valid">
              <v-row>
                <v-col>
                  <v-select
                    v-model="archive.journal_type_code"
                    :items="journalTypeOptions"
                    :label="$t('label.journal_type')"
                    :rules="isChildUploadMode ? [] : [rules.required]"
                    dense
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-select
                    v-model="archive.journal_idx"
                    :items="publishedJournalOptions"
                    :label="$t('label.linked_journal')"
                    :rules="isChildUploadMode ? [] : []"
                    dense
                    clearable
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-if="isChildUploadMode">
                <v-col>
                  <v-select
                    v-model="archive.journal_content_idx"
                    :items="paperOptions"
                    :label="$t('label.paper_select')"
                    dense
                    clearable
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-if="isChildUploadMode && selectedPaper">
                <v-col>
                  <v-alert
                    v-if="selectedPaper.file_path"
                    type="info"
                    dense
                    text
                  >
                    {{ $t("txt.paper_file_exists") }}:
                    {{ selectedPaperFileName }}
                    <v-btn
                      icon
                      small
                      color="primary"
                      class="ml-2"
                      @click="handleDownloadPaperFile"
                    >
                      <v-icon small>mdi-download</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      color="red"
                      class="ml-1"
                      @click="handleDeletePaperFile"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </v-alert>
                  <v-alert v-else type="info" dense text>
                    {{ $t("txt.paper_file_missing") }}
                  </v-alert>
                </v-col>
              </v-row>
              <v-row v-if="showParentSelect">
                <v-col>
                  <v-select
                    v-model="archive.parent_idx"
                    :items="parentArchiveOptions"
                    :label="$t('label.parent_archive')"
                    dense
                    clearable
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-if="isChildUploadMode">
                <v-col>
                  <v-alert type="info" dense text>
                    {{ $t("txt.child_upload_mode") }}
                  </v-alert>
                </v-col>
              </v-row>
              <v-row v-if="!isChildUploadMode">
                <v-col>
                  <v-text-field
                    v-model="archive.title"
                    :label="$t('label.title')"
                    type="text"
                    :rules="[rules.required]"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="!isChildUploadMode">
                <v-col>
                  <v-text-field
                    v-model="archive.down_file_name"
                    :label="$t('label.file_name')"
                    type="text"
                    :rules="[rules.required]"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="!isChildUploadMode">
                <v-col>
                  <v-text-field
                    v-model="archive.order_number"
                    :label="$t('label.order_number')"
                    type="number"
                    :rules="[rules.required]"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="!archive.idx">
                <v-col>
                  <upload-file
                    :key="uploadFileKey"
                    :initialFilePath="archive.file_path"
                    @update-saved-file-path="updateSavedFilePath"
                    @cleared="removeFilePath"
                  ></upload-file>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions class="mt-3">
            <v-spacer></v-spacer>
            <custom-button
              v-if="canStartChildUpload"
              @click="startChildUploadFromCurrent"
              :label="$t('button.child_upload')"
              color="secondary"
              size="lg"
              gradient
              rounded
            ></custom-button>
            <custom-button
              @click="handleSubmit"
              :label="$t('button.save')"
              :disabled="isSaveDisabled"
              color="primary"
              size="lg"
              gradient
              rounded
            ></custom-button>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PaginatedTable from "@/components/Tables/PaginatedTable.vue";
import UploadFile from "@/components/Forms/UploadFile.vue";
import CardHeader from "@/components/cards/CardHeader.vue";
import CustomButton from "@/components/common/buttons/CustomButton.vue";
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";

import { getJournalArchiveColumnsColumns } from "@/views/AdminView/columns";
import {
  confirmChildUpload,
  deletePopup,
  savePopup,
  success,
} from "@/views/AdminView/popups";

import { archiveApi, fileApi, journalApi, journalContentApi } from "@/api/api";

export default {
  components: {
    CardHeader,
    CustomButton,
    PaginatedTable,
    UploadFile,
    LoadingSpinner,
  },
  data() {
    return {
      // boolean
      isLoading: false,
      isChildUploadMode: false,

      // fields & columns
      tableColumns: getJournalArchiveColumnsColumns({
        edit: this.handleEdit,
        download: this.handleDownload,
        delete: this.handleDelete,
      }),

      category: "J",
      filterJournalType: "all",
      archive: {
        idx: null,
        title: null,
        image_name: null,
        down_file_name: null,
        file_name: null,
        order_number: null,
        category: "J",
        journal_type_code: "jnl",
        journal_content_idx: null,
        parent_idx: null,
        journal_idx: null,
        file_path: "",
      },
      parentArchives: [],
      publishedJournals: [],
      journalContents: [],
      archives: {
        page_info: {
          page: 1,
          size: 5,
          total_elements: 0,
          total_pages: 1,
        },
        elements: [],
      },
      valid: false,
      rules: {
        required: (v) => !!v || "This field is required",
      },
    };
  },
  computed: {
    journalTypeOptions() {
      return [
        { text: this.$t("label.journal_type_abst"), value: "abst" },
        { text: this.$t("label.journal_type_procs"), value: "procs" },
        { text: this.$t("label.journal_type_jnl"), value: "jnl" },
      ];
    },
    journalTypeFilterOptions() {
      return [
        { text: this.$t("label.filter_all"), value: "all" },
        ...this.journalTypeOptions,
      ];
    },
    parentArchiveOptions() {
      return (this.parentArchives || [])
        .filter((item) => {
          const typeCode = item?.journal_type_code || item?.journal_type?.code;
          const isJournal = !typeCode || typeCode === "jnl";
          return !item?.parent_idx && isJournal;
        })
        .map((item) => ({
          text: item?.title || `#${item?.idx}`,
          value: item?.idx,
        }));
    },
    publishedJournalOptions() {
      return (this.publishedJournals || []).map((item) => ({
        text: item?.title || item?.title_eng || `#${item?.idx}`,
        value: item?.idx,
      }));
    },
    showParentSelect() {
      return this.archive?.journal_type_code === "jnl";
    },
    canStartChildUpload() {
      return (
        !!this.archive?.idx && this.archive?.journal_type_code === "jnl"
      );
    },
    paperOptions() {
      return (this.journalContents || []).map((item) => {
        const title = item?.title || item?.title_eng || `#${item?.idx}`;
        const suffix = item?.file_path ? ` (${this.$t("txt.file_exists")})` : "";
        return {
          text: `${title}${suffix}`,
          value: item?.idx,
        };
      });
    },
    selectedPaper() {
      return (this.journalContents || []).find(
        (item) => item?.idx === this.archive?.journal_content_idx
      );
    },
    selectedPaperFileName() {
      const path = this.selectedPaper?.file_path || "";
      return path ? path.split("/").pop() : "";
    },
    uploadFileKey() {
      return `${this.archive?.journal_content_idx || "none"}:${this.archive?.file_path || ""}`;
    },
    isSaveDisabled() {
      if (this.isChildUploadMode) {
        return (
          !this.archive?.journal_idx ||
          !this.archive?.journal_content_idx ||
          !this.archive?.file_path
        );
      }
      return !this.valid;
    },
  },
  methods: {
    /**
     * *******************************************************************************************
     *  버튼 액션
     * *******************************************************************************************
     */
    // 수정 버튼 액션
    async handleEdit(archive) {
      const result = await this.fetchArchive(archive);
      if (result) {
        if (!this.archive?.journal_type_code && this.archive?.journal_type?.code) {
          this.archive.journal_type_code = this.archive.journal_type.code;
        }
        if (!this.archive?.journal_idx && this.archive?.journal?.idx) {
          this.archive.journal_idx = this.archive.journal.idx;
        }
        if (!this.archive?.parent_idx && this.archive?.parent?.idx) {
          this.archive.parent_idx = this.archive.parent.idx;
        }
        this.isChildUploadMode = false;
      }
    },

    // Save button action
    async handleSubmit() {
      if (this.archive.file_path == null || this.archive.file_path == "") {
        await this.notifyVue("top", "center", "파일을 업로드해주세요.");
        return;
      }

      const isConfirmed = await savePopup();

      if (!isConfirmed) {
        return;
      }

      if (this.isChildUploadMode) {
        const isUpdated = await this.updateJournalContentFile();
        if (isUpdated) {
          await success();
          await this.resetChildUploadForm();
        } else {
          this.notifyVue("top", "center", this.$t("alert.failed_request"));
        }
        return;
      }

      const isCreate = this.archive.idx == null;
      const createdArchive = isCreate
        ? await this.createArchive()
        : null;
      const isSuccess = isCreate ? !!createdArchive : await this.updateArchive();

      if (isSuccess) {
        await this.fetchArchivesByCategory();
        await this.fetchParentArchives();
        await success();

        if (
          isCreate &&
          this.archive?.journal_type_code === "jnl" &&
          !this.archive?.parent_idx
        ) {
          const wantsChildUpload = await confirmChildUpload();
          if (wantsChildUpload) {
            const parentArchive =
              createdArchive && createdArchive.idx
                ? createdArchive
                : this.findParentArchiveFallback(createdArchive);
            this.startChildUploadFromParent(parentArchive);
            return;
          }
        }

        await this.initArchive();
      }
    },
    async handleDownload(archive) {
      await this.downloadFile(archive);
    },
    async handleDelete(archive) {
      const isConfirmed = await deletePopup(archive);

      if (!isConfirmed) {
        return;
      }

      const isDeleted = await this.deleteArchive(archive);

      if (isDeleted) {
        await success();
        this.fetchArchivesByCategory();
      }
    },

    /**
     * *******************************************************************************************
     *  API 요청
     * *******************************************************************************************
     */
    async fetchArchivesByCategory() {
      try {
        this.isLoading = true;
        const params = {
          category: this.category,
          page: this.archives.page_info.page,
          size: this.archives.page_info.size,
        };
        if (this.filterJournalType && this.filterJournalType !== "all") {
          params.journal_type_code = this.filterJournalType;
        }
        const response = await archiveApi.showAllByCateogory(params);
        if (response && response.status === 200) {
          this.archives = response.data.response;
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchArchive(archive) {
      try {
        this.isLoading = true;
        const response = await archiveApi.showArchive(archive);
        if (response && response.status === 200) {
          this.archive = response.data.response;
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async createArchive() {
      try {
        this.isLoading = true;
        const payload = this.buildArchivePayload();
        const response = await archiveApi.createArchive(payload);
        if (response && (response.status === 201 || response.status === 200)) {
          return (
            response.data?.response ||
            response.data?.data ||
            response.data ||
            null
          );
        }
        return null;
      } catch {
        return null;
      } finally {
        this.isLoading = false;
      }
    },
    async deleteArchive(archive) {
      try {
        this.isLoading = true;
        const response = await archiveApi.deleteArchive(archive);
        if (response && response.status === 204) {
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async updateArchive() {
      try {
        this.isLoading = true;
        const payload = this.buildArchivePayload();
        const response = await archiveApi.updateArchive(payload);
        if (response && response.status === 200) {
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async downloadFile(archive) {
      try {
        this.isLoading = true;
        const params = {
          file_name: archive.file_path.split("/").pop(),
        };

        const response = await fileApi.fileDownload(params);

        if (response && response.status === 200) {
          const mimeType = response.headers["content-type"];
          const blob = new Blob([response.data], { type: mimeType });
          const fileURL = URL.createObjectURL(blob);

          // 자동 열기
          if (mimeType === "application/pdf" || mimeType.startsWith("image/")) {
            const link = document.createElement("a");
            link.href = fileURL;
            link.setAttribute("download", archive.down_file_name); // 다운로드 파일 이름 설정
            link.setAttribute("target", "_blank"); // 새 창에서 열기
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          } else {
            // 다운로드로 처리
            const link = document.createElement("a");
            link.href = fileURL;
            link.setAttribute("download", archive.down_file_name); // 다운로드 파일 이름 설정
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          }
        }
      } catch (error) {
        alert("파일 다운로드에 실패했습니다. 잠시후 다시 시도해주세요.");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * *******************************************************************************************
     *  기타 메서드
     * *******************************************************************************************
     */
    /**
     * Pagination 타입 명세
     * @typedef {Object} Conditions
     * @property {string} page - 페이지
     * @property {string} size - 사이즈
     * @property {string} total_elements - 전체 데이터 수
     * @property {string} total_pages - 전체 페이지 수
     */

    /**
     * 페이지네이션 업데이트 메서드
     *
     * @description: 페이징 정보가 변경되면 하위 컴포넌트에서 정보를 받아 업데이트 및 API 요청
     * @params {Pagination} pagination
     */
    updatePagination(pagination) {
      this.archives.page_info.size = pagination.size;
      this.archives.page_info.page = pagination.page;

      this.fetchArchivesByCategory();
    },
    handleFilterChange() {
      this.archives.page_info.page = 1;
      this.fetchArchivesByCategory();
    },
    updateSavedFilePath(newPath) {
      this.archive.file_path = newPath;
    },
    buildArchivePayload() {
      return {
        ...this.archive,
        category: this.category,
      };
    },
    async fetchParentArchives() {
      try {
        const params = {
          category: this.category,
          page: 1,
          size: 1000,
          journal_type_code: "jnl",
        };
        const response = await archiveApi.showAllByCateogory(params);
        if (response && response.status === 200) {
          const data = response.data.response;
          this.parentArchives = Array.isArray(data?.elements)
            ? data.elements
            : [];
        }
      } catch {
        this.parentArchives = [];
      }
    },
    async fetchPublishedJournals() {
      try {
        const params = { page: 1, size: 1000 };
        const response = await journalApi.showJournalForPublish(params);
        if (response && response.status === 200) {
          const data = response.data.response;
          const elements = Array.isArray(data?.elements) ? data.elements : [];
          this.publishedJournals = elements.filter((item) => item?.is_published);
        }
      } catch {
        this.publishedJournals = [];
      }
    },
    async fetchJournalContents() {
      try {
        if (!this.archive?.journal_idx) {
          this.journalContents = [];
          return;
        }
        const pathParams = { journal_idx: this.archive.journal_idx };
        const queryParams = { category: "paper", is_published: true };
        const response =
          await journalContentApi.showJournalContentByJournalAndCategory({
            pathParams,
            queryParams,
          });
        if (response.status === 200) {
          const allContents = response.data.response || [];
          this.journalContents = allContents.filter(
            (item) =>
              item?.category !== "empty" &&
              item?.title !== "공페이지" &&
              item?.title !== "빈페이지"
          );
        }
      } catch {
        this.journalContents = [];
      }
    },
    async updateJournalContentFile() {
      if (!this.archive?.journal_idx) {
        this.notifyVue("top", "center", this.$t("alert.select_journal"));
        return false;
      }
      if (!this.archive?.journal_content_idx) {
        this.notifyVue("top", "center", this.$t("alert.select_paper"));
        return false;
      }
      if (!this.archive?.file_path) {
        this.notifyVue("top", "center", "파일을 업로드해주세요.");
        return false;
      }
      try {
        const payload = {
          idx: this.archive.journal_content_idx,
          file_path: this.archive.file_path,
        };
        const response = await journalContentApi.updateJournalContent(payload);
        return response && response.status === 200;
      } catch {
        return false;
      }
    },
    async handleDownloadPaperFile() {
      try {
        const filePath = this.selectedPaper?.file_path;
        if (!filePath) return;
        const response = await fileApi.fileDownload({ file_name: filePath });
        if (response && response.status === 200) {
          const mimeType = response.headers["content-type"];
          const blob = new Blob([response.data], { type: mimeType });
          const fileURL = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = fileURL;
          link.setAttribute("download", this.selectedPaperFileName || "paper.pdf");
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          URL.revokeObjectURL(fileURL);
        }
      } catch (error) {
        this.notifyVue("top", "center", this.$t("alert.failed_request"));
      }
    },
    async handleDeletePaperFile() {
      try {
        const filePath = this.selectedPaper?.file_path;
        if (!filePath || !this.archive?.journal_content_idx) return;
        await fileApi.deleteFile({ file_name: filePath });
        const response = await journalContentApi.updateJournalContent({
          idx: this.archive.journal_content_idx,
          file_path: "",
        });
        if (response && response.status === 200) {
          const updated = this.journalContents.map((item) =>
            item?.idx === this.archive.journal_content_idx
              ? { ...item, file_path: "" }
              : item
          );
          this.journalContents = updated;
          this.archive.file_path = "";
          await success();
        }
      } catch (error) {
        this.notifyVue("top", "center", this.$t("alert.failed_request"));
      }
    },
    startChildUploadFromCurrent() {
      this.startChildUploadFromParent(this.archive);
    },
    startChildUploadFromParent(parentArchive) {
      const parentIdx = parentArchive?.idx || null;
      const journalIdx = parentArchive?.journal_idx || null;
      this.archive = {
        idx: null,
        title: null,
        down_file_name: null,
        order_number: null,
        category: this.category,
        journal_type_code: "jnl",
        journal_content_idx: null,
        parent_idx: parentIdx,
        journal_idx: journalIdx,
        file_path: "",
      };
      this.isChildUploadMode = true;
      this.fetchJournalContents();
    },
    findParentArchiveFallback(createdArchive) {
      if (!createdArchive) return null;
      const match = (this.parentArchives || []).find((item) => {
        if (createdArchive?.idx && item?.idx === createdArchive.idx) return true;
        if (createdArchive?.file_path && item?.file_path === createdArchive.file_path) return true;
        if (createdArchive?.title && item?.title === createdArchive.title) return true;
        return false;
      });
      return match || null;
    },
    initArchive() {
      this.archive = {
        idx: null,
        title: null,
        image_name: null,
        down_file_name: null,
        file_name: null,
        order_number: null,
        category: this.category,
        journal_type_code: "jnl",
        journal_content_idx: null,
        parent_idx: null,
        journal_idx: null,
        file_path: "",
      };
      this.isChildUploadMode = false;
    },
    async resetChildUploadForm() {
      this.archive.journal_content_idx = null;
      this.archive.file_path = "";
      this.isChildUploadMode = true;
    },
    removeFilePath() {
      this.archive.file_path = "";
    },
    notifyVue(verticalAlign, horizontalAlign, alertMessage, alert_type) {
      this.$notify({
        timeout: 5000,
        message: alertMessage,
        icon: "priority_high",
        type: alert_type ? alert_type : "danger",
        horizontalAlign: horizontalAlign,
        verticalAlign: verticalAlign,
      });
    },
  },
  watch: {
    "archive.journal_idx": {
      immediate: false,
      handler() {
        this.archive.journal_content_idx = null;
        this.fetchJournalContents();
      },
    },
    "archive.journal_content_idx": {
      immediate: false,
      handler() {
        const filePath = this.selectedPaper?.file_path || "";
        this.archive.file_path = filePath;
      },
    },
  },
  mounted() {
    this.fetchArchivesByCategory();
    this.initArchive();
    this.fetchParentArchives();
    this.fetchPublishedJournals();
  },
};
</script>

<style lang="css"></style>
